import { HttpClient } from '@angular/common/http';
import { computed, effect, inject } from '@angular/core';
import { docData, Firestore } from '@angular/fire/firestore';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { PccAuthService } from '@ss/shared/auth';
import { UserStore } from '@ss/shared/user';
import { firebaseConfig } from '@ss/typings/config';
import { Enterprise, Facility, NtaUser } from '@ss/typings/facility';
import { ADR_ROLES, ELIGIBLE_ROUNDER_ROLES, NewUser, Role, ROUND_ROLES, SCREEN_ROLES } from '@ss/typings/nta-hunter';
import { PccFacility } from '@ss/typings/pcc';
import { collection, CollectionReference, doc, DocumentReference } from 'firebase/firestore';
import { filter, pipe, switchMap, tap } from 'rxjs';

interface State {
	facilityId: string;
	facility: Facility;
	enterprise: Enterprise;
	loadingProperties: {
		facility: boolean;
		enterprise: boolean;
		pccFacility: boolean;
		unlinkedPccFacilities: boolean;
		users: boolean;
	};
	pccFacility: PccFacility;
	unlinkedPccFacilities: PccFacility[];
}

const initialState: State = {
	facilityId: '',
	facility: new Facility(),
	enterprise: new Enterprise(),
	loadingProperties: {
		facility: false,
		enterprise: false,
		pccFacility: false,
		unlinkedPccFacilities: false,
		users: false,
	},
	pccFacility: new PccFacility(),
	unlinkedPccFacilities: [],
};

export const NtaFacilityStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withMethods((store, firestore = inject(Firestore), http = inject(HttpClient)) => ({
		updateUser(user: NtaUser, role: Role) {
			return http.put(`${firebaseConfig.functionsUrl}/ntaApi/user/${user.userId}/role`, { role });
		},
		addUser(newUser: NewUser) {
			patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, users: true } }));
			return http.put<NtaUser>(`${firebaseConfig.functionsUrl}/ntaApi/user`, newUser).pipe(tap(() => patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, users: false } }))));
		},
		removeUser(user: NtaUser) {
			patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, users: true } }));
			return http.delete(`${firebaseConfig.functionsUrl}/ntaApi/user/${user.userId}`).pipe(tap(() => patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, users: false } }))));
		},
		loadFacility: rxMethod<string>(pipe(
			filter((facilityId) => !!facilityId),
			tap(() => patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, facility: true } }))),
			switchMap((facilityId) => docData<Facility>(doc(collection(firestore, '/facilities') as CollectionReference<Facility>, facilityId) as DocumentReference<Facility>, { idField: 'facilityId' })),
			tap((facility) => patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, facility: false }, facility: new Facility(facility) })))
		)),
		loadPccFacility: rxMethod<{ pccFacilityId: number; isLoggedInWithPcc: boolean }>(pipe(
			filter(({ pccFacilityId, isLoggedInWithPcc }) => !!pccFacilityId && isLoggedInWithPcc),
			tap(() => patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, pccFacility: true } }))),
			switchMap(({ pccFacilityId }) => http.get<PccFacility>(`${firebaseConfig.functionsUrl}/ntaApi/pcc/facility/${pccFacilityId}?includeOptionalFields=pickList`)),
			tap((pccFacility) => patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, pccFacility: false }, pccFacility: new PccFacility(pccFacility) })))
		)),
		loadEnterprise: rxMethod<{ enterpriseId: string }>(pipe(
			filter(({ enterpriseId }) => !!enterpriseId),
			tap(() => patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, enterprise: true } }))),
			switchMap(({ enterpriseId }) => docData<Enterprise>(doc(collection(firestore, '/enterprises') as CollectionReference<Enterprise>, enterpriseId) as DocumentReference<Enterprise>, { idField: 'enterpriseId' })),
			tap((enterprise) => patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, enterprise: false }, enterprise: new Enterprise(enterprise) })))
		)),
		loadUnlinkedPccFacilities: rxMethod<{ isLoggedInWithPcc: boolean }>(pipe(
			filter(({ isLoggedInWithPcc }) => isLoggedInWithPcc),
			tap(() => patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, unlinkedPccFacilities: true } }))),
			switchMap(() => http.get<PccFacility[]>(`${firebaseConfig.functionsUrl}/ntaApi/pcc/facilities/unlinked`)),
			tap((unlinkedPccFacilities) => patchState(store, (state) => ({ ...state, loadingProperties: { ...state.loadingProperties, unlinkedPccFacilities: false }, unlinkedPccFacilities: unlinkedPccFacilities.map((f) => new PccFacility(f)) })))
		))
	})),
	withComputed(({ facility, pccFacility, loadingProperties }, userStore = inject(UserStore), pccAuthService = inject(PccAuthService)) => ({
		isAdmin: computed(() => {
			const claims = userStore.userClaims();
			const currentFacility = facility();
			const hasAdminAccessThroughCorporation = currentFacility.corporationId && claims.corporationIds.includes(currentFacility.corporationId);

			return claims.isSuperAdmin ||
				claims.enterpriseIds.includes(currentFacility.enterpriseId) ||
				hasAdminAccessThroughCorporation ||
				claims.facilities[currentFacility.facilityId] === 'admin';
		}),
		currentFacilityRole: computed(() => {
			const claims = userStore.userClaims();
			const currentFacility = facility();
			const hasAdminAccessThroughCorporation = currentFacility.corporationId && claims.corporationIds.includes(currentFacility.corporationId);

			if (claims.isSuperAdmin || claims.enterpriseIds.includes(currentFacility.enterpriseId) || hasAdminAccessThroughCorporation) return 'admin';

			return claims.facilities[currentFacility.facilityId] || '';
		}),
		sortedUsers: computed(() => facility().ntaUsers.slice().sort((a, b) => {
			if (a.role === 'admin' && b.role !== 'admin') return -1;
			if (a.role !== 'admin' && b.role === 'admin') return 1;
			return a.name?.localeCompare(b.name);
		})),
		hasSingleAdmin: computed(() => facility().ntaUsers.filter((u) => u.role === 'admin').length === 1),
		loadEnterpriseParams: computed(() => ({ enterpriseId: facility().enterpriseId })),
		loading: computed(() => Object.values(loadingProperties()).some((value) => value)),
		loaded: computed(() => Object.values(loadingProperties()).every((value) => !value)),
		facilityLoaded: computed(() => !loadingProperties().facility && !!facility().facilityId),
		loadUnlinkedPccFacilitiesParams: computed(() => ({ isLoggedInWithPcc: pccAuthService.isLoggedInWithPcc() })),
		loadPccFacilityParams: computed(() => ({ pccFacilityId: facility().pccFacilityId, isLoggedInWithPcc: pccAuthService.isLoggedInWithPcc() })),
		hasPccEnabled: computed<boolean>(() => pccAuthService.isLoggedInWithPcc() && !!facility().pccFacilityId),
		canImportPccMds: computed(() => facility().features.includes('ADR_HIPPS_MAP')),
		isPccLinked: computed(() => !!facility().pccFacilityId),
		isPccReadyToLink: computed(() => facility().isPccReadyToLink),
		canUploadDocuments: computed(() => !facility().features.includes('NO_PCC')),
		uploadDocumentStatus: computed(() => {
			if (!facility().pccFacilityId) return 'not-linked';
			if (!pccAuthService.isLoggedInWithPcc()) return 'not-logged-in';
			if (!pccFacility().isUploadEnabled) return 'disabled';
			if (!pccFacility().pickList.documentCategories) return 'not-authorized';
			return 'ready';
		}),
	})),
	withComputed(({ facility, currentFacilityRole }) => ({
		isSimpliroundsBaseAccount: computed(() => currentFacilityRole() === 'simplirounds-base'),
		eligibleRounders: computed(() => facility().ntaUsers
			.filter((user) => ELIGIBLE_ROUNDER_ROLES.includes(user.role as Role))
			.sort((a, b) => a.name.localeCompare(b.name)) as NtaUser[]),
		facilityLayoutEnabled: computed(() => facility().features.includes('FACILITY_LAYOUT')),
		simpliroundsEnabled: computed(() => facility().features.includes('SIMPLIROUNDS') && ROUND_ROLES.includes(currentFacilityRole() as Role)),
		simpliMDSEnabled: computed(() => facility().features.includes('SIMPLIMDS') && SCREEN_ROLES.includes(currentFacilityRole() as Role)),
		simpliADREnabled: computed(() => facility().features.includes('SIMPLIADR') && ADR_ROLES.includes(currentFacilityRole() as Role)),
	})),
	withHooks({
		async onInit(store, userStore = inject(UserStore)) {
			effect(() => {
				patchState(store, { facilityId: userStore.currentFacilityId() });
			});
			store.loadFacility(store.facilityId);
			store.loadEnterprise(store.loadEnterpriseParams);
			store.loadPccFacility(store.loadPccFacilityParams);
			store.loadUnlinkedPccFacilities(store.loadUnlinkedPccFacilitiesParams);
		}
	}),
);
